import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaArrowLeft, FaTrash, FaSync, FaDatabase, FaCloud, FaExclamationTriangle, FaUser } from 'react-icons/fa';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { storageUsageService, StorageUsage } from '../utils/storageUsageService';
import { storageCleanupService } from '../utils/storageCleanupService';
import { storageNotificationService } from '../utils/storageNotificationService';
import '../admin.css';
import './ActivityTracker.css';

interface Organization {
  code: string;
  name: string;
}

const ActivityTracker: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgCode, setSelectedOrgCode] = useState<string>('');
  const [storageUsage, setStorageUsage] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const loadOrganizations = useCallback(async () => {
    try {
      const orgsSnapshot = await getDocs(
        query(collection(db, 'organizations'), where('isActive', '==', true))
      );
      const orgs: Organization[] = [];
      orgsSnapshot.forEach((doc) => {
        const data = doc.data();
        orgs.push({
          code: data.code || doc.id,
          name: data.name || doc.id,
        });
      });
      setOrganizations(orgs);
      setSelectedOrgCode((current) => current || orgs[0]?.code || '');
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  }, []);

  const loadStorageUsage = useCallback(async () => {
    if (!selectedOrgCode) return;
    
    try {
      setLoading(true);
      const usage = await storageUsageService.getStorageUsage(selectedOrgCode);
      setStorageUsage(usage);
    } catch (error) {
      console.error('Error loading storage usage:', error);
      setStorageUsage(null);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgCode]);

  useEffect(() => {
    loadOrganizations();
    
    // Check storage notifications on load
    const checkNotifications = async () => {
      try {
        setTimeout(async () => {
          await storageNotificationService.checkAndNotify();
        }, 2000); // 2 second delay to let data load
      } catch (error) {
        console.error('Error checking storage notifications:', error);
      }
    };
    
    checkNotifications();
  }, [loadOrganizations]);

  useEffect(() => {
    if (selectedOrgCode) {
      loadStorageUsage();
    }
  }, [selectedOrgCode, loadStorageUsage]);

  const handleCalculateUsage = async () => {
    if (!selectedOrgCode) return;
    
    try {
      setCalculating(true);
      const usage = await storageUsageService.calculateStorageUsage(selectedOrgCode);
      setStorageUsage(usage);
    } catch (error) {
      console.error('Error calculating storage usage:', error);
      alert('Failed to calculate storage usage. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  const handleClearStorage = async () => {
    if (!selectedOrgCode) return;
    
    try {
      setClearing(true);
      setShowConfirmModal(false);
      
      const result = await storageCleanupService.clearAllStorage(selectedOrgCode);
      
      if (result.success) {
        alert(
          `Storage cleared successfully for ${storageUsage?.organizationName}!\n\n` +
          `Deleted:\n` +
          `- ${result.deleted.posts} posts\n` +
          `- ${result.deleted.messages} messages\n` +
          `- ${result.deleted.profilePictures} profile pictures\n` +
          `- ${result.deleted.events} events\n` +
          `- ${result.deleted.cloudinaryImages} Cloudinary images`
        );
        
        // Reset storage usage
        await storageUsageService.resetUsage(selectedOrgCode);
        await loadStorageUsage();
      } else {
        alert(`Storage cleanup completed with errors:\n${result.errors.join('\n')}`);
      }
    } catch (error) {
      console.error('Error clearing storage:', error);
      alert('Failed to clear storage. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const showClearButton = storageUsage && storageUsage.total.percentage >= 80;

  return (
    <div className="admin-theme activity-admin">
      <div className="activity-shell">
        <div className="activity-topbar">
          <button className="back-to-admin" onClick={() => navigate('/admin')}>
            <FaArrowLeft /> Back to admin
          </button>
          <div className="activity-brand">
            <span className="activity-brand-mark">P</span>
            PEAR <span>ADMIN</span>
          </div>
          <div className="activity-user">
            <div className="user-menu">
              <FaUser className="user-icon" />
              <span>{currentUser?.email}</span>
            </div>
            <button onClick={logout} className="logout-btn">Logout</button>
          </div>
        </div>

        <div className="activity-header">
          <div>
            <h1>
              <FaDatabase className="header-icon" />
              Storage Activity Tracker
            </h1>
            <p>Monitor cached usage quickly, then recalculate only when you need fresh numbers.</p>
          </div>
        </div>

        {/* Organization Selector */}
        <div className="org-selector-section">
          <label htmlFor="org-select" className="org-select-label">Select Organization:</label>
          <select
            id="org-select"
            value={selectedOrgCode}
            onChange={(e) => setSelectedOrgCode(e.target.value)}
            className="org-select"
          >
            {organizations.map((org) => (
              <option key={org.code} value={org.code}>
                {org.name} ({org.code})
              </option>
            ))}
          </select>
          <button
            onClick={handleCalculateUsage}
            disabled={calculating || !selectedOrgCode}
            className="btn-secondary"
          >
            <FaSync className={calculating ? 'spinning' : ''} />
            {calculating ? 'Calculating...' : 'Recalculate fresh usage'}
          </button>
        </div>

        {loading ? (
          <div className="activity-loading">
            <div className="activity-spinner"></div>
            <div>
              <strong>Loading cached usage</strong>
              <span>This should only take a moment.</span>
            </div>
          </div>
        ) : !storageUsage ? (
          <div className="error-message">
            No cached storage usage is available yet. Choose an organization and run a fresh recalculation.
          </div>
        ) : (
          <>
            {/* Warning Banner */}
            {showClearButton && (
              <div className="warning-banner">
                <FaExclamationTriangle />
                <span>
                  Storage usage is at {storageUsage.total.percentage.toFixed(1)}% for {storageUsage.organizationName}. 
                  Consider clearing storage to free up space.
                </span>
              </div>
            )}

            {/* Storage Overview */}
            <div className="storage-overview">
              <div className="storage-card">
                <div className="storage-card-header">
                  <FaCloud className="storage-icon" />
                  <h2>{storageUsage.organizationName} - Total Storage Usage</h2>
                </div>
                
                {/* Total Storage Progress Bar */}
                <div className="progress-section">
                  <div className="progress-header">
                    <span className="progress-label">Total Storage</span>
                    <span className="progress-percentage">
                      {storageUsage.total.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="progress-bar-large">
                    <div
                      className={`progress-fill-large ${storageUsage.total.percentage >= 80 ? 'warning' : storageUsage.total.percentage >= 60 ? 'caution' : ''}`}
                      style={{ width: `${Math.min(100, storageUsage.total.percentage)}%` }}
                    />
                  </div>
                  <div className="progress-stats">
                    <span>{storageUsageService.formatBytes(storageUsage.total.used)}</span>
                    <span>/</span>
                    <span>{storageUsageService.formatBytes(storageUsage.total.limit)}</span>
                    <span className="remaining">
                      ({storageUsageService.formatBytes(storageUsage.total.limit - storageUsage.total.used)} remaining)
                    </span>
                  </div>
                </div>

                {/* Cloudinary Progress Bar */}
                <div className="progress-section">
                  <div className="progress-header">
                    <span className="progress-label">Cloudinary Storage</span>
                    <span className="progress-percentage">
                      {storageUsage.cloudinary.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill cloudinary ${storageUsage.cloudinary.percentage >= 80 ? 'warning' : ''}`}
                      style={{ width: `${Math.min(100, storageUsage.cloudinary.percentage)}%` }}
                    />
                  </div>
                  <div className="progress-stats">
                    <span>{storageUsageService.formatBytes(storageUsage.cloudinary.used)}</span>
                    <span>/</span>
                    <span>{storageUsageService.formatBytes(storageUsage.cloudinary.limit)}</span>
                  </div>
                </div>

                {/* Firebase Progress Bar */}
                <div className="progress-section">
                  <div className="progress-header">
                    <span className="progress-label">Firebase Storage</span>
                    <span className="progress-percentage">
                      {storageUsage.firebase.percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className={`progress-fill firebase ${storageUsage.firebase.percentage >= 80 ? 'warning' : ''}`}
                      style={{ width: `${Math.min(100, storageUsage.firebase.percentage)}%` }}
                    />
                  </div>
                  <div className="progress-stats">
                    <span>{storageUsageService.formatBytes(storageUsage.firebase.used)}</span>
                    <span>/</span>
                    <span>{storageUsageService.formatBytes(storageUsage.firebase.limit)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Storage Breakdown by Type */}
            <div className="type-breakdown">
              <h3>Storage Breakdown by Type</h3>
              <div className="breakdown-list">
                <div className="breakdown-item">
                  <div className="breakdown-item-header">
                    <span className="breakdown-color" style={{ backgroundColor: '#5041BC' }} />
                    <span className="breakdown-name">Posts</span>
                  </div>
                  <span className="breakdown-size">
                    {storageUsageService.formatBytes(storageUsage.breakdown.posts)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <div className="breakdown-item-header">
                    <span className="breakdown-color" style={{ backgroundColor: '#43BE83' }} />
                    <span className="breakdown-name">Messages</span>
                  </div>
                  <span className="breakdown-size">
                    {storageUsageService.formatBytes(storageUsage.breakdown.messages)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <div className="breakdown-item-header">
                    <span className="breakdown-color" style={{ backgroundColor: '#FFA726' }} />
                    <span className="breakdown-name">Profile Pictures</span>
                  </div>
                  <span className="breakdown-size">
                    {storageUsageService.formatBytes(storageUsage.breakdown.profilePictures)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <div className="breakdown-item-header">
                    <span className="breakdown-color" style={{ backgroundColor: '#EA8F95' }} />
                    <span className="breakdown-name">Events</span>
                  </div>
                  <span className="breakdown-size">
                    {storageUsageService.formatBytes(storageUsage.breakdown.events)}
                  </span>
                </div>
                <div className="breakdown-item">
                  <div className="breakdown-item-header">
                    <span className="breakdown-color" style={{ backgroundColor: '#8487A2' }} />
                    <span className="breakdown-name">Other</span>
                  </div>
                  <span className="breakdown-size">
                    {storageUsageService.formatBytes(storageUsage.breakdown.other)}
                  </span>
                </div>
              </div>
            </div>

            {/* Clear Storage Button */}
            {showClearButton && (
              <div className="clear-storage-section">
                <button
                  onClick={() => setShowConfirmModal(true)}
                  disabled={clearing}
                  className="btn-danger btn-large"
                >
                  <FaTrash />
                  {clearing ? 'Clearing Storage...' : 'Clear Storage'}
                </button>
                <p className="clear-warning">
                  This will delete all posts, messages, profile pictures, and event images for {storageUsage.organizationName}.
                  This action cannot be undone.
                </p>
              </div>
            )}

            {/* Last Updated */}
            <div className="last-updated">
              Last updated: {storageUsage.lastUpdated.toLocaleString()}
            </div>
          </>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Confirm Storage Clear</h3>
              <p>
                Are you sure you want to clear all storage for {storageUsage?.organizationName}? 
                This will permanently delete:
              </p>
              <ul>
                <li>All posts and their images</li>
                <li>All messages and media</li>
                <li>All profile pictures</li>
                <li>All event images</li>
              </ul>
              <p className="modal-warning">This action cannot be undone.</p>
              <div className="modal-actions">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearStorage}
                  disabled={clearing}
                  className="btn-danger"
                >
                  {clearing ? 'Clearing...' : 'Clear Storage'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTracker;
